/**
 * OTKS1D — qlty024 SummaryEmailOTKList Standard 1.1 contract helpers.
 * REMOTE_PROGRESSIVE; server export; audit trailing after business.
 */

export const SUMMARY_EMAIL_OTK_MASK_PAGE = 'SummaryEmailOTKList';

export const SUMMARY_EMAIL_OTK_REFRESH_EVENT = 'summary-email-otk-list-refresh';

export const SUMMARY_EMAIL_OTK_LIST_TYPE = 'REMOTE_PROGRESSIVE';

export const SUMMARY_EMAIL_OTK_PAGED_URL = '/api/summary_emails/paged';

export const SUMMARY_EMAIL_OTK_PRINT_URL = '/api/summary_emails/export/print';

export const SUMMARY_EMAIL_OTK_XLSX_URL = '/api/summary_emails/export/xlsx';

/** Structural fields always forced visible; never offered in Nastavenie masky. */
export const SUMMARY_EMAIL_OTK_REQUIRED_VISIBILITY_FIELDS = Object.freeze(['id']);

/** Configurable data/audit fields shown in Nastavenie masky (actions + ID excluded). */
export const SUMMARY_EMAIL_OTK_MASK_CONFIGURABLE_FIELDS = Object.freeze([
  'name',
  'custom_submodules',
  'status',
  'interval',
  'regularity',
  'custom_regularity',
  'email_time',
  'custom_employees',
  'custom_other_emails',
  'created_at',
  'updated_at',
]);

export const SUMMARY_EMAIL_OTK_DEFAULT_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  ...SUMMARY_EMAIL_OTK_MASK_CONFIGURABLE_FIELDS.reduce((acc, field) => {
    acc[field] = true;
    return acc;
  }, {}),
});

export function getSummaryEmailOTKMaskFieldLabel(field, t) {
  const map = {
    name: 'SummaryEmailOTKList.Column.Name',
    custom_submodules: 'SummaryEmailOTKList.Column.Submodules',
    status: 'SummaryEmailOTKList.Column.Type',
    interval: 'SummaryEmailOTKList.Column.Interval',
    regularity: 'SummaryEmailOTKList.Column.Regularity',
    custom_regularity: 'SummaryEmailOTKList.Column.RegularityDetail',
    email_time: 'SummaryEmailOTKList.Column.EmailTime',
    custom_employees: 'SummaryEmailOTKList.Column.Employees',
    custom_other_emails: 'SummaryEmailOTKList.Column.OtherEmails',
    created_at: 'OtkOptionCatalog.Column.CreatedAt',
    updated_at: 'OtkOptionCatalog.Column.UpdatedAt',
  };
  const key = map[field];
  if (!key) return field;
  const translated = typeof t === 'function' ? t(key) : key;
  return translated && translated !== key ? translated : key;
}

/** Canonical structural field order for effective-column tests. */
export const SUMMARY_EMAIL_OTK_CANONICAL_FIELDS = Object.freeze([
  'rowSelection',
  'editor',
  'id',
  'name',
  'custom_submodules',
  'status',
  'interval',
  'regularity',
  'custom_regularity',
  'email_time',
  'custom_employees',
  'custom_other_emails',
  'created_at',
  'updated_at',
]);

/**
 * Merge saved user/standard mask visibility with current registry defaults.
 * New audit/business fields are appended as visible; unknown stale keys dropped.
 */
export function reconcileSummaryEmailOTKColumnVisibility(saved) {
  const next = { ...SUMMARY_EMAIL_OTK_DEFAULT_COLUMN_VISIBILITY };
  if (saved && typeof saved === 'object') {
    for (const field of SUMMARY_EMAIL_OTK_MASK_CONFIGURABLE_FIELDS) {
      if (Object.prototype.hasOwnProperty.call(saved, field)) {
        next[field] = !!saved[field];
      }
    }
  }
  if (!Object.values(next).some(Boolean)) {
    return { ...SUMMARY_EMAIL_OTK_DEFAULT_COLUMN_VISIBILITY };
  }
  return next;
}

export function emitSummaryEmailOTKListRefresh() {
  if (typeof window === 'undefined' || !window.dispatchEvent) return false;
  window.dispatchEvent(new CustomEvent(SUMMARY_EMAIL_OTK_REFRESH_EVENT));
  return true;
}

/** Canonical REMOTE_PROGRESSIVE toolbar search query token for paged/export. */
export const SUMMARY_EMAIL_OTK_TOOLBAR_SEARCH_PARAM = 'toolbar_search';

/**
 * Append toolbar_search to request/export params when non-empty.
 * @param {Record<string, unknown>} extra
 * @param {unknown} searchValue
 * @returns {Record<string, unknown>}
 */
export function appendSummaryEmailOTKToolbarSearch(extra, searchValue) {
  const next = extra && typeof extra === 'object' ? { ...extra } : {};
  const normalized = searchValue == null ? '' : String(searchValue).trim();
  if (normalized !== '') {
    next[SUMMARY_EMAIL_OTK_TOOLBAR_SEARCH_PARAM] = normalized;
  }
  return next;
}

/** Sanitize column layout attributes: drop unknown fields, keep action+ID leading. */
export function reconcileSummaryEmailOTKColumnLayout(attributes) {
  if (!Array.isArray(attributes) || attributes.length === 0) return null;
  const allowed = new Set(SUMMARY_EMAIL_OTK_CANONICAL_FIELDS);
  const cleaned = attributes.filter((c) => c && allowed.has(c.field));
  if (cleaned.length === 0) return null;
  const leading = ['rowSelection', 'editor', 'id'];
  const byField = new Map(cleaned.map((c) => [c.field, c]));
  const head = leading.map((f) => byField.get(f)).filter(Boolean);
  const rest = cleaned.filter((c) => !leading.includes(c.field));
  // Ensure audit fields exist in layout when missing from stale masks.
  for (const field of ['created_at', 'updated_at']) {
    if (!rest.some((c) => c.field === field) && !head.some((c) => c.field === field)) {
      rest.push({ field, visible: true });
    }
  }
  return [...head, ...rest];
}
